import { Box, Flex, Text } from '@chakra-ui/react'
import { useFormColors } from './FormThemeContext'

interface StepConfig {
  key: string
  label: string
  hasError?: boolean
}

interface WizardStepperProps<T extends string> {
  steps: StepConfig[]
  activeStep: T
  completedSteps: Set<string>
  onStepClick: (step: T) => void
}

export function WizardStepper<T extends string>({
  steps,
  activeStep,
  completedSteps,
  onStepClick,
}: WizardStepperProps<T>) {
  const c = useFormColors()

  return (
    <>
      {/* ── Desktop ── */}
      <Flex
        alignItems="center"
        py={2}
        px={1}
        gap={0}
        display={{ base: 'none', md: 'flex' }}
      >
        {steps.map((step, index) => {
          const isActive = activeStep === step.key
          const isCompleted = completedSteps.has(step.key)
          const stepNumber = index + 1
          const isLast = index === steps.length - 1
          const lineColor = isCompleted ? c.accent : c.border

          return (
            <Flex key={step.key} alignItems="center" flex={isLast ? '0 0 auto' : '1 1 0'}>
              <Flex
                as="button" {...{ type: 'button' }}
                alignItems="center"
                gap="10px"
                onClick={() => onStepClick(step.key as T)}
                flexShrink={0}
                bg={isCompleted ? c.accent : isActive ? 'white' : 'transparent'}
                border={isActive ? '2px solid' : isCompleted ? 'none' : '1px solid'}
                borderColor={isActive ? c.accent : c.border}
                borderRadius="full"
                pl={isCompleted ? '4px' : '3px'}
                pr={isCompleted ? 4 : isActive ? 4 : 3}
                py={isCompleted ? '4px' : '3px'}
                cursor="pointer"
                transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
                _hover={{
                  shadow: 'sm',
                  transform: 'translateY(-1px)',
                }}
                position="relative"
              >
                {/* Number / Check circle */}
                <Flex
                  w="28px"
                  h="28px"
                  borderRadius="full"
                  bg={
                    isCompleted ? 'rgba(255,255,255,0.2)'
                    : isActive ? c.accent
                    : c.bgHover
                  }
                  color={
                    isCompleted ? 'white'
                    : isActive ? 'white'
                    : c.textMuted
                  }
                  alignItems="center"
                  justifyContent="center"
                  fontSize="xs"
                  fontWeight="700"
                  flexShrink={0}
                  transition="all 0.25s"
                >
                  {isCompleted ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    stepNumber
                  )}
                </Flex>

                {/* Label */}
                <Text
                  fontSize="12px"
                  fontWeight={isActive || isCompleted ? '600' : '500'}
                  color={isCompleted ? 'white' : isActive ? c.text : c.textMuted}
                  whiteSpace="nowrap"
                  lineHeight="1"
                >
                  {step.label}
                </Text>

                {/* Error indicator */}
                {step.hasError && (
                  <Box
                    position="absolute"
                    top="-2px"
                    right="-2px"
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg="#c8442f"
                    border="2px solid white"
                  />
                )}
              </Flex>

              {/* Connector */}
              {!isLast && (
                <Box
                  flex="1"
                  h="2px"
                  bg={lineColor}
                  mx={2}
                  borderRadius="full"
                  minW="10px"
                  transition="background 0.3s"
                />
              )}
            </Flex>
          )
        })}
      </Flex>

      {/* ── Mobile ── */}
      <Flex
        alignItems="center"
        py={2}
        px={1}
        gap={0}
        display={{ base: 'flex', md: 'none' }}
      >
        {steps.map((step, index) => {
          const isActive = activeStep === step.key
          const isCompleted = completedSteps.has(step.key)
          const stepNumber = index + 1
          const isLast = index === steps.length - 1
          const lineColor = isCompleted ? c.accent : c.border

          return (
            <Flex key={step.key} alignItems="center" flex={isLast ? '0 0 auto' : '1 1 0'}>
              <Flex
                as="button" {...{ type: 'button' }}
                alignItems="center"
                gap={isActive ? '6px' : 0}
                onClick={() => onStepClick(step.key as T)}
                flexShrink={0}
                bg={isCompleted ? c.accent : isActive ? 'white' : 'transparent'}
                border={isActive ? '2px solid' : isCompleted ? 'none' : '1px solid'}
                borderColor={isActive ? c.accent : c.border}
                borderRadius="full"
                p={isActive ? '3px' : isCompleted ? '0' : '0'}
                pr={isActive ? 3 : 0}
                cursor="pointer"
                transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
              >
                <Flex
                  w={isActive ? '24px' : isCompleted ? '26px' : '26px'}
                  h={isActive ? '24px' : isCompleted ? '26px' : '26px'}
                  borderRadius="full"
                  bg={
                    isCompleted ? c.accent
                    : isActive ? c.accent
                    : c.bgHover
                  }
                  color={
                    isCompleted ? 'white'
                    : isActive ? 'white'
                    : c.textMuted
                  }
                  alignItems="center"
                  justifyContent="center"
                  fontSize="10px"
                  fontWeight="700"
                  flexShrink={0}
                >
                  {isCompleted ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    stepNumber
                  )}
                </Flex>

                {isActive && (
                  <Text fontSize="10px" fontWeight="700" color={c.text} whiteSpace="nowrap" lineHeight="1">
                    {step.label.length > 10 ? step.label.substring(0, 10) + '..' : step.label}
                  </Text>
                )}
              </Flex>

              {!isLast && (
                <Box
                  flex="1"
                  h="2px"
                  bg={lineColor}
                  mx="3px"
                  borderRadius="full"
                  minW="4px"
                  transition="background 0.3s"
                />
              )}
            </Flex>
          )
        })}
      </Flex>
    </>
  )
}
